import type { FormState, SkeletonChange } from './certificate-form'
import { useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_CERTIFICATE_TEMPLATE, GQL_UPDATE_CERTIFICATE_TEMPLATE } from '~/gql/certificates/certificates'
import { readFileAsDataUri } from '~/helpers/files/read-file-as-data-uri'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'
import { EMPTY_FORM, formFromTemplate, isFormDirty, logoFileError, skeletonFileError, toTemplateInput } from './certificate-form'
import { openCertificatePreview } from './certificate-preview'

/**
 * Le formulaire d'un modèle d'attestation (#162) : chargé depuis le modèle
 * choisi, enregistré sur lui, prévisualisé avec son logo et son fond, et
 * `dirty` tant qu'il diffère de ce qui est enregistré.
 */
export function useCertificateForm(templateId: string, onSaved: () => Promise<unknown>) {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_CERTIFICATE_TEMPLATE, { variables: { id: templateId } })
  const [updateTemplate, { loading: saving }] = useMutation(GQL_UPDATE_CERTIFICATE_TEMPLATE)
  const [saved, setSaved] = useState<FormState>(EMPTY_FORM)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [skeleton, setSkeleton] = useState<SkeletonChange>(undefined)
  const [previewing, setPreviewing] = useState(false)
  const skeletonActive = skeleton === undefined ? (data?.certificateTemplate?.hasSkeleton ?? false) : skeleton !== ''

  useSyncOnChange(data?.certificateTemplate, (template) => {
    const loaded = formFromTemplate(template as Partial<Record<keyof FormState, unknown>>)
    setSkeleton(undefined)
    setSaved(loaded)
    setForm(loaded)
  })

  const set = <K extends keyof FormState>(key: K) => (value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  /** Lit un fichier choisi après l'avoir validé ; un refus se dit, rien ne change. */
  const readFile = async (file: File, error: string | null, apply: (dataUri: string) => void, readErrorKey: string) => {
    if (error) {
      toast.error(t(error))
      return
    }
    try {
      apply(await readFileAsDataUri(file))
    } catch {
      toast.error(t(readErrorKey))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateTemplate({ variables: { id: templateId, input: toTemplateInput(form, skeleton) } })
      await Promise.all([refetch(), onSaved()])
      toast.success(t('certificates.saved'))
    } catch {
      toast.error(t('certificates.saveError'))
    }
  }

  const handlePreview = async () => {
    setPreviewing(true)
    try {
      await openCertificatePreview(templateId, toTemplateInput(form, skeleton))
    } catch {
      toast.error(t('certificates.previewError'))
    } finally {
      setPreviewing(false)
    }
  }

  return {
    form,
    set,
    loading,
    saving,
    previewing,
    skeletonActive,
    dirty: !loading && isFormDirty(form, saved, skeleton),
    removeSkeleton: () => setSkeleton(''),
    handleLogoFile: (file: File) => readFile(file, logoFileError(file), set('logo'), 'certificates.logo.readError'),
    handleSkeletonFile: (file: File) => readFile(file, skeletonFileError(file), setSkeleton, 'certificates.skeleton.readError'),
    handleSubmit,
    handlePreview,
  }
}
